/**
 * GiftDialog.jsx — Send a shop item as a gift to another reader.
 *
 * Flow
 *   1. Search recipient by username/email prefix (≥2 chars debounced).
 *   2. Pick recipient from results.
 *   3. (Optional) Add a 240-char message.
 *   4. Confirm — calls /api/shop/gift, wallet refreshes, 60s undo toast.
 *
 * Self-serve refund matches /shop/purchase/{id}/refund (same 60s window;
 * inventory is reverted on the RECIPIENT side automatically).
 */
import React, { useEffect, useState } from 'react';
import { Gift, Coins, Gem, Search, Loader2, X } from 'lucide-react';
import { authFetch } from '../utils/authUtils';
import { Button } from './ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from './ui/dialog';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_MSG = 240;

const GiftDialog = ({ open, onOpenChange, item, onSent }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [recipient, setRecipient] = useState(null);
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);

  // Reset whenever the dialog opens for a new item.
  useEffect(() => {
    if (open) {
      setQuery(''); setResults([]); setRecipient(null); setMessage('');
    }
  }, [open]);

  // Debounced search.
  useEffect(() => {
    if (!open) return undefined;
    if (recipient) return undefined; // don't search after pick
    const q = query.trim();
    if (q.length < 2) { setResults([]); return undefined; }
    let cancelled = false;
    const handle = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await authFetch(`${BACKEND_URL}/api/shop/gift/lookup?q=${encodeURIComponent(q)}`);
        if (!r.ok) return;
        const d = await r.json();
        if (!cancelled) setResults(d.results || []);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(handle); };
  }, [query, open, recipient]);

  const send = async () => {
    if (!recipient || !item) return;
    setBusy(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/api/shop/gift`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          recipient_user_id: recipient.user_id,
          item_id: item.item_id,
          message: message.trim(),
        }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || `HTTP ${r.status}`);

      toast.success(`Gift sent to ${recipient.display_name}`, {
        description: `${item.name} delivered to their inventory.`,
        action: {
          label: 'Undo',
          onClick: async () => {
            try {
              const ur = await authFetch(`${BACKEND_URL}/api/shop/purchase/${data.purchase_id}/refund`, { method: 'POST' });
              const ud = await ur.json();
              if (!ur.ok) throw new Error(ud.detail || 'Refund failed');
              toast.success('Gift refunded — coins restored.');
              onSent?.(data.wallet);
            } catch (err) {
              toast.error(err.message || 'Could not undo');
            }
          },
        },
        duration: 60000,
      });
      onSent?.(data.wallet);
      onOpenChange(false);
    } catch (err) {
      toast.error(err.message || 'Failed to send gift');
    } finally {
      setBusy(false);
    }
  };

  if (!item) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!busy) onOpenChange(v); }}>
      <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-md" data-testid="gift-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Gift className="w-5 h-5 text-rose-300" />
            Send as gift
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            <span className="block text-white text-sm">
              <strong>{item.name}</strong>{' '}
              {item.price_coins > 0 && (
                <span className="inline-flex items-center gap-0.5 font-bold text-amber-200 ml-1">
                  <Coins className="w-3.5 h-3.5" /> {item.price_coins}
                </span>
              )}
              {item.price_gems > 0 && (
                <span className="inline-flex items-center gap-0.5 font-bold text-cyan-200 ml-1">
                  <Gem className="w-3.5 h-3.5" /> {item.price_gems}
                </span>
              )}
            </span>
            <span className="block text-xs text-slate-400 mt-1">
              Coins are deducted from your wallet. The recipient gets the item instantly. You can undo within 60 seconds.
            </span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {/* Recipient picker */}
          {!recipient ? (
            <div>
              <label className="text-[11px] uppercase tracking-wider text-slate-400 font-bold">Recipient</label>
              <div className="relative mt-1">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search by name or email…"
                  className="w-full bg-slate-800 border border-slate-700 rounded-md pl-8 pr-3 py-2 text-sm text-white placeholder:text-slate-500"
                  data-testid="gift-search-input"
                  autoFocus
                />
              </div>
              <div className="mt-2 max-h-44 overflow-y-auto rounded-md border border-slate-700 divide-y divide-slate-800 bg-slate-800/50">
                {searching ? (
                  <div className="p-3 text-center text-xs text-slate-400">
                    <Loader2 className="w-3 h-3 inline-block animate-spin mr-1" /> Searching…
                  </div>
                ) : results.length === 0 ? (
                  <div className="p-3 text-center text-xs text-slate-500">
                    {query.trim().length < 2 ? 'Type 2+ characters to search.' : 'No matches.'}
                  </div>
                ) : (
                  results.map((u) => (
                    <button
                      key={u.user_id}
                      type="button"
                      onClick={() => setRecipient(u)}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-slate-700/60 flex items-center justify-between gap-2"
                      data-testid={`gift-pick-${u.user_id}`}
                    >
                      <span className="font-semibold text-white truncate">{u.display_name}</span>
                      <span className="text-[10px] text-slate-500 shrink-0">
                        {u.email_hint && <span className="mr-1">{u.email_hint}</span>}
                        {u.role}
                      </span>
                    </button>
                  ))
                )}
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between gap-2 p-2.5 rounded-md bg-rose-500/10 border border-rose-500/40" data-testid="gift-recipient-card">
              <div className="min-w-0">
                <p className="text-sm font-bold text-white truncate">{recipient.display_name}</p>
                <p className="text-[11px] text-rose-200/80">{recipient.role}</p>
              </div>
              <button
                type="button"
                onClick={() => { setRecipient(null); setQuery(''); }}
                className="p-1 rounded hover:bg-slate-700 text-slate-400 hover:text-white"
                aria-label="Change recipient"
                data-testid="gift-change-recipient"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Message */}
          {recipient && (
            <div>
              <label className="text-[11px] uppercase tracking-wider text-slate-400 font-bold">
                Message <span className="text-slate-600 font-normal">(optional)</span>
              </label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value.slice(0, MAX_MSG))}
                placeholder="Thanks for the recommendations!"
                rows={3}
                className="w-full mt-1 bg-slate-800 border border-slate-700 rounded-md px-3 py-2 text-sm text-white"
                data-testid="gift-message-input"
              />
              <p className="text-[10px] text-slate-500 text-right mt-0.5">{message.length}/{MAX_MSG}</p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={busy}
            className="border-slate-600 text-slate-200"
            data-testid="gift-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={send}
            disabled={busy || !recipient}
            className="bg-gradient-to-r from-rose-500 to-amber-400 hover:from-rose-400 hover:to-amber-300 text-slate-900 font-bold disabled:opacity-50"
            data-testid="gift-send"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Gift className="w-4 h-4 mr-2" /> Send gift</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default GiftDialog;
